// Compute the smallest character range in `after` that bounds the
// changed content relative to `before`. Stable, O(n) common-prefix
// + common-suffix scan — good enough for the "flash the changed
// region" flash. Not a proper diff; returns a single range that
// covers every byte of divergence.
//
// Returns null when strings are identical, or when either is empty.

export type DiffRange = { from: number; to: number };

export function diffRange(before: string, after: string): DiffRange | null {
  if (before === after) return null;
  if (after.length === 0) return null;

  const n = Math.min(before.length, after.length);

  // Common prefix.
  let prefix = 0;
  while (prefix < n && before.charCodeAt(prefix) === after.charCodeAt(prefix)) {
    prefix++;
  }

  // Common suffix — but don't cross into the prefix region.
  let suffix = 0;
  const maxSuffix = Math.min(before.length - prefix, after.length - prefix);
  while (
    suffix < maxSuffix &&
    before.charCodeAt(before.length - 1 - suffix) ===
      after.charCodeAt(after.length - 1 - suffix)
  ) {
    suffix++;
  }

  let from = prefix;
  let to = after.length - suffix;

  // Rotate the range leftward toward a preceding newline when the
  // greedy prefix has overshot a line boundary. This happens when the
  // inserted region's leading chars also appear at the start of the
  // following line — e.g. inserting `const y = 2\n` between two other
  // `const …` lines. Greedy prefix swallows the shared `const ` and
  // the resulting range straddles the line break (`y = 2\nconst `)
  // instead of bounding the inserted line. We only rotate when from
  // isn't already at a line boundary; once we hit one, stop.
  if (
    from > 0 &&
    to > from &&
    after.charCodeAt(from - 1) !== 10 /* '\n' */
  ) {
    while (
      from > 0 &&
      to > from &&
      after.charCodeAt(from - 1) === after.charCodeAt(to - 1)
    ) {
      from--;
      to--;
      if (after.charCodeAt(from - 1) === 10) break;
    }
  }

  if (to <= from) {
    // Purely deletion-only case — nothing to highlight in `after`.
    // Collapse to a 1-char caret at the deletion point, clamped.
    const caret = Math.min(from, after.length);
    return { from: caret, to: Math.min(caret + 1, after.length) };
  }
  return { from, to };
}
